import { isObject } from 'radash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAcquireRecordLockMutation, useHeartbeatRecordLockMutation, useReleaseRecordLockMutation } from 'store/services/flow/agendaRecords';
import type { RootState } from 'store';
import { HeartbeatReason, type AcquireRecordLockResponse } from 'types';
import { releaseRecordLockKeepalive } from 'utils/agendaRecord/recordLock';
import { isRecord } from 'utils/typeChecks';

const HEARTBEAT_INTERVAL_MS = 30000;
const ACTIVITY_TIMEOUT_MS = 30000;
const LOCK_EXPIRING_THRESHOLD_MS = 60000;

type UseRecordLockParams = {
  agendaId: string;
  recordId: string;
  onLockExpired: () => void;
  onLockCountdownExpired?: () => void;
};

type UseRecordLockReturn = {
  isLockAcquired: boolean;
  isLockAcquiring: boolean;
  lockToken: string | null;
  lockConflict: AcquireRecordLockResponse | null;
  lockExpiresAtUtc: string | null;
  isLockRenewing: boolean;
  releaseLock: () => Promise<void>;
  beginUploadingFiles: () => void;
  endUploadingFiles: () => void;
  continueEditing: () => Promise<void>;
  setIsLockExpiringModalOpen: (isOpen: boolean) => void;
};

type SessionRef = {
  lockToken: string | null;
  reason: HeartbeatReason;
  intervalId: ReturnType<typeof setInterval> | null;
  uploadingCount: number;
  expiresAt: number | null;
  lastActivityAt: number;
  isPageActive: boolean;
  isLockExpiringModalOpen: boolean;
  onLockExpired: () => void;
  onLockCountdownExpired: () => void;
};

const isNewRecord = (recordId: string) => recordId.startsWith('new');

// Lock ownership is shared per record across hook instances: React tears an effect down while its
// acquire request is still in flight (StrictMode, remount, record switch), and the backend hands the
// same token back to the same owner. A torn-down run therefore has to know whether a newer run holds
// or is about to receive its token before it releases anything.
type RecordLockOwnership = {
  runId: number;
  settled: boolean;
  token: string | null;
  unclaimedToken: string | null;
};

const recordLockOwnership = new Map<string, RecordLockOwnership>();
let lastLockRunId = 0;

const lockKeyOf = (agendaId: string, recordId: string) => `${agendaId}/${recordId}`;

const isAcquireRecordLockResponse = (value: unknown): value is AcquireRecordLockResponse => {
  if (!isRecord(value)) return false;

  return (
    typeof value.success === 'boolean' &&
    (value.lockToken === null || typeof value.lockToken === 'string') &&
    (value.lockedBy === null || typeof value.lockedBy === 'string') &&
    (value.expiresAtUtc === null || typeof value.expiresAtUtc === 'string')
  );
};

export const useRecordLock = ({
  agendaId,
  recordId,
  onLockExpired,
  onLockCountdownExpired = onLockExpired,
}: UseRecordLockParams): UseRecordLockReturn => {
  const [isLockAcquiring, setIsLockAcquiring] = useState(false);
  const [lockToken, setLockToken] = useState<string | null>(null);
  const [lockConflict, setLockConflict] = useState<AcquireRecordLockResponse | null>(null);
  const [lockExpiresAtUtc, setLockExpiresAtUtc] = useState<string | null>(null);
  const [isLockRenewing, setIsLockRenewing] = useState(false);

  const [acquireRecordLock] = useAcquireRecordLockMutation();
  const [releaseRecordLock] = useReleaseRecordLockMutation();
  const [heartbeatRecordLock] = useHeartbeatRecordLockMutation();

  const auth = useSelector((state: RootState) => state.auth);
  const authRef = useRef(auth);
  authRef.current = auth;

  const sessionRef = useRef<SessionRef>({
    lockToken: null,
    reason: HeartbeatReason.Editing,
    expiresAt: null,
    intervalId: null,
    uploadingCount: 0,
    lastActivityAt: Date.now(),
    isPageActive: !document.hidden && document.hasFocus(),
    isLockExpiringModalOpen: false,
    onLockExpired,
    onLockCountdownExpired,
  });

  const clearHeartbeatInterval = useCallback(() => {
    if (sessionRef.current.intervalId !== null) {
      clearInterval(sessionRef.current.intervalId);
      sessionRef.current.intervalId = null;
    }
  }, []);

  const clearLockState = useCallback(() => {
    sessionRef.current.lockToken = null;
    sessionRef.current.uploadingCount = 0;
    sessionRef.current.reason = HeartbeatReason.Editing;
    sessionRef.current.expiresAt = null;
    sessionRef.current.isLockExpiringModalOpen = false;
    setLockToken(null);
    setLockExpiresAtUtc(null);
  }, []);

  const updateLockExpiry = useCallback((expiresAtUtc: string | null) => {
    const expiresAt = expiresAtUtc ? Date.parse(expiresAtUtc) : Number.NaN;
    sessionRef.current.expiresAt = Number.isFinite(expiresAt) ? expiresAt : null;
    setLockExpiresAtUtc(Number.isFinite(expiresAt) ? expiresAtUtc : null);
  }, []);

  const releaseLock = useCallback(async () => {
    const { lockToken: token } = sessionRef.current;
    if (!token || isNewRecord(recordId)) return;

    try {
      await releaseRecordLock({ agendaId, recordId, lockToken: token }).unwrap();
      // the session may already hold a newer lock when this release resolves late
      if (sessionRef.current.lockToken === token) clearLockState();
    } catch {
      // the token is kept, so a later release attempt can still clear the lock
    }
  }, [agendaId, clearLockState, recordId, releaseRecordLock]);

  const sendHeartbeat = useCallback(
    async (isActiveOverride?: boolean) => {
      const {
        lockToken: token,
        reason,
        expiresAt,
        lastActivityAt,
        isPageActive,
        isLockExpiringModalOpen,
        onLockExpired: notifyExpired,
      } = sessionRef.current;
      const now = Date.now();

      if (!token || (expiresAt !== null && now >= expiresAt)) return;

      const isExpiring = isLockExpiringModalOpen || (expiresAt !== null && now >= expiresAt - LOCK_EXPIRING_THRESHOLD_MS);
      const isActive = isActiveOverride ?? (!isExpiring && isPageActive && now - lastActivityAt < ACTIVITY_TIMEOUT_MS);

      try {
        const result = await heartbeatRecordLock({
          agendaId,
          recordId,
          lockToken: token,
          reason,
          isActive,
        }).unwrap();

        if (sessionRef.current.lockToken !== token) return;

        if (!result.isValid) {
          clearHeartbeatInterval();
          clearLockState();
          notifyExpired();
        } else {
          const currentExpiry = sessionRef.current.expiresAt;

          if (!result.expiresAtUtc) return;
          if (currentExpiry !== null && Date.parse(result.expiresAtUtc) < currentExpiry) return;
          if (isActiveOverride !== true && currentExpiry !== null && Date.now() >= currentExpiry - LOCK_EXPIRING_THRESHOLD_MS) {
            return;
          }

          updateLockExpiry(result.expiresAtUtc);
        }
      } catch {
        // heartbeat failures are handled on the next tick or via release
      }
    },
    [agendaId, clearHeartbeatInterval, clearLockState, heartbeatRecordLock, recordId, updateLockExpiry]
  );

  const startHeartbeat = useCallback(() => {
    clearHeartbeatInterval();
    sessionRef.current.intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
  }, [clearHeartbeatInterval, sendHeartbeat]);

  const continueEditing = useCallback(async () => {
    sessionRef.current.lastActivityAt = Date.now();
    setIsLockRenewing(true);
    try {
      await sendHeartbeat(true);
    } finally {
      setIsLockRenewing(false);
    }
  }, [sendHeartbeat]);

  const setIsLockExpiringModalOpen = useCallback((isOpen: boolean) => {
    sessionRef.current.isLockExpiringModalOpen = isOpen;
  }, []);

  useEffect(() => {
    if (!lockToken) return;

    const handleActivity = () => {
      if (!sessionRef.current.isPageActive || sessionRef.current.isLockExpiringModalOpen) return;

      sessionRef.current.lastActivityAt = Date.now();
    };

    const handlePageActivity = () => {
      sessionRef.current.isPageActive = !document.hidden && document.hasFocus();
      if (sessionRef.current.isPageActive) {
        handleActivity();
      }
    };

    const handleBlur = () => {
      sessionRef.current.isPageActive = false;
    };

    const activityEvents = ['pointermove', 'pointerdown', 'keydown', 'input', 'scroll', 'wheel', 'touchstart'] as const;
    activityEvents.forEach((event) => window.addEventListener(event, handleActivity, { capture: true, passive: true }));
    window.addEventListener('focus', handlePageActivity);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handlePageActivity);

    return () => {
      activityEvents.forEach((event) => window.removeEventListener(event, handleActivity, true));
      window.removeEventListener('focus', handlePageActivity);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handlePageActivity);
    };
  }, [lockToken]);

  const beginUploadingFiles = useCallback(() => {
    sessionRef.current.uploadingCount += 1;
    sessionRef.current.reason = HeartbeatReason.UploadingFiles;
  }, []);

  const endUploadingFiles = useCallback(() => {
    sessionRef.current.uploadingCount = Math.max(0, sessionRef.current.uploadingCount - 1);
    if (sessionRef.current.uploadingCount === 0) {
      sessionRef.current.reason = HeartbeatReason.Editing;
    }
  }, []);

  useEffect(() => {
    sessionRef.current.onLockExpired = onLockExpired;
    sessionRef.current.onLockCountdownExpired = onLockCountdownExpired;
  }, [onLockExpired, onLockCountdownExpired]);

  useEffect(() => {
    if (!lockToken || !lockExpiresAtUtc) return;

    const expiresAt = Date.parse(lockExpiresAtUtc);
    const expirationTimeout = setTimeout(
      () => {
        const { lockToken: token, expiresAt: currentExpiresAt, onLockCountdownExpired: notifyExpired } = sessionRef.current;
        if (token !== lockToken || currentExpiresAt !== expiresAt) return;

        clearHeartbeatInterval();
        clearLockState();
        notifyExpired();
      },
      Math.max(0, expiresAt - Date.now())
    );

    return () => clearTimeout(expirationTimeout);
  }, [lockToken, lockExpiresAtUtc, clearHeartbeatInterval, clearLockState]);

  useEffect(() => {
    if (isNewRecord(recordId)) return;

    let cancelled = false;
    const requestAgendaId = agendaId;
    const requestRecordId = recordId;
    const lockKey = lockKeyOf(requestAgendaId, requestRecordId);
    const runId = (lastLockRunId += 1);

    recordLockOwnership.set(lockKey, { runId, settled: false, token: null, unclaimedToken: null });

    const acquireLock = async () => {
      setIsLockAcquiring(true);
      setLockConflict(null);

      try {
        const response = await acquireRecordLock({
          agendaId: requestAgendaId,
          recordId: requestRecordId,
          suppressErrorToast: true,
        }).unwrap();

        if (cancelled) {
          // A newer run may own this record now, in this instance or another one. Releasing is only
          // correct once that run has settled without this token; while it is still in flight it may
          // receive the same token (the backend returns it to the same owner), so leave the token for
          // it to keep or release.
          const ownership = recordLockOwnership.get(lockKey);

          if (ownership !== undefined && ownership.runId !== runId) {
            if (ownership.token === response.lockToken) return;

            if (!ownership.settled) {
              ownership.unclaimedToken = response.lockToken;
              return;
            }
          }

          if (response.success && response.lockToken) {
            releaseRecordLock({
              agendaId: requestAgendaId,
              recordId: requestRecordId,
              lockToken: response.lockToken,
            });
          }
          return;
        }

        if (response.success && response.lockToken) {
          const ownership = recordLockOwnership.get(lockKey);

          if (ownership?.runId === runId) {
            ownership.token = response.lockToken;
            // A superseded acquisition of this record always receives this same token, so there is
            // nothing left to release for it.
            ownership.unclaimedToken = null;
          }

          sessionRef.current.lockToken = response.lockToken;
          sessionRef.current.lastActivityAt = Date.now();
          sessionRef.current.isPageActive = !document.hidden && document.hasFocus();
          sessionRef.current.isLockExpiringModalOpen = false;
          setLockToken(response.lockToken);
          updateLockExpiry(response.expiresAtUtc);
          setLockConflict(null);
          startHeartbeat();
        } else {
          setLockConflict(response);
        }
      } catch (error) {
        if (cancelled) return;

        const conflictDataFromError = isObject(error) && 'data' in error && isAcquireRecordLockResponse(error.data) ? error.data : null;

        if (conflictDataFromError?.success === false) {
          setLockConflict(conflictDataFromError);
          return;
        }

        setLockConflict(null);
      } finally {
        if (!cancelled) {
          const ownership = recordLockOwnership.get(lockKey);

          if (ownership?.runId === runId) {
            ownership.settled = true;

            // This run could not take the record, so the token a superseded run left behind has no
            // owner left to release it and would stay locked until its TTL expires.
            if (!ownership.token && ownership.unclaimedToken) {
              releaseRecordLock({ agendaId: requestAgendaId, recordId: requestRecordId, lockToken: ownership.unclaimedToken });
              ownership.unclaimedToken = null;
            }
          }

          setIsLockAcquiring(false);
        }
      }
    };

    acquireLock();

    const handlePageHide = (event: PageTransitionEvent) => {
      const { lockToken: token } = sessionRef.current;
      // A persisted page goes into the back/forward cache; the edit session survives it.
      if (event.persisted || !token || isNewRecord(recordId)) return;

      void releaseRecordLockKeepalive(authRef.current, { agendaId, recordId, lockToken: token });
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      cancelled = true;
      clearHeartbeatInterval();
      window.removeEventListener('pagehide', handlePageHide);

      const ownership = recordLockOwnership.get(lockKey);

      if (ownership?.runId === runId) {
        // An unclaimed token from a superseded run would otherwise stay locked until its TTL.
        if (ownership.unclaimedToken) {
          releaseRecordLock({ agendaId: requestAgendaId, recordId: requestRecordId, lockToken: ownership.unclaimedToken });
        }

        recordLockOwnership.delete(lockKey);
      }

      releaseLock();
    };
  }, [acquireRecordLock, agendaId, clearHeartbeatInterval, recordId, releaseLock, releaseRecordLock, startHeartbeat, updateLockExpiry]);

  return {
    isLockAcquired: Boolean(lockToken),
    isLockAcquiring,
    lockToken,
    lockConflict,
    lockExpiresAtUtc,
    isLockRenewing,
    continueEditing,
    releaseLock,
    beginUploadingFiles,
    endUploadingFiles,
    setIsLockExpiringModalOpen,
  };
};
